import MapCache from 'lodash/_MapCache'

type Callable<P extends ReadonlyArray<any>, R> = { (...p: P): R }

type Params<P> = {
  timeToLive: number
  cacheSize?: number
  resolver?: (p: P) => string
}

const DEFAULT_RESOLVER = <P extends ReadonlyArray<any>>(p: P) =>
  p?.[0] === undefined ? 'UNDEFINED' : p?.[0] === null ? 'NULL' : p?.[0]

type MemoizedFunction<P extends ReadonlyArray<any>, R> = Callable<P, R> & {
  readonly clearCache: () => void
}

type Memoize = { <P extends ReadonlyArray<any>, R>(c: Callable<P, R>, params: Params<P>): MemoizedFunction<P, R> }

const memoize: Memoize = function <P extends ReadonlyArray<any>, R>(originalFunction, params) {
  const resolver = params?.resolver ?? DEFAULT_RESOLVER
  const timeToLive = params?.timeToLive ?? 60 * 1000 /* 1m */
  const cacheSize = params?.cacheSize ?? 500
  let _cache: MapCache = null
  let _cacheExp: number
  let _cacheKeys: Array<string> = null

  const getCache = (): typeof _cache => {
    if (!_cache || _cacheExp < Date.now()) {
      // expirou cache
      clearCache()
    }
    return _cache
  }
  const clearCache = () => {
    _cache = new MapCache()
    _cacheExp = Date.now() + timeToLive
    _cacheKeys = []
  }
  const memoizedFunction: Callable<P, R> = function (...args) {
    const key = resolver(args)
    const cache = getCache()

    if (cacheSize > 0 && !cache.has(key)) {
      while (_cacheKeys.length >= cacheSize) {
        // estorou o cache - abrir espaço!
        const [poppedKey] = _cacheKeys.splice(0, 1)
        cache.delete(poppedKey)
      }
    }
    if (!cache.has(key)) {
      _cacheKeys.push(key)
      cache.set(key, originalFunction(...args))
    }
    return cache.get(key)
  }
  memoizedFunction['clearCache'] = clearCache.bind(memoizedFunction)
  return memoizedFunction as MemoizedFunction<P, R>
}

export default memoize
